import React, { useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import styled from '@emotion/native';
import { ApolloError, useMutation } from '@apollo/client';
import { ADMIN_UPDATE_STYLIST_BOOKING } from '@/shared/queries/admin';
import {
  AdminStylist,
  Country,
  MutationAdminUpdateStylistBookingArgs,
  MutationResult,
} from '@/shared/types/generated';
import Button from '@/shared/components/Button';
import Checkbox from '@/shared/components/Checkbox';
import AddressForm, {
  AddressFormData,
  HelpText,
  InputContainer,
  InputLabel as FieldLabel,
  TextInput as FieldInput,
} from '@/components/editProfileForm/Address';
import { mapLocationTypeToEnum } from '@/components/editProfileForm/addressMappers';
import SentryService from '@/services/sentry/Sentry.service';
import useStore from '@/stores/useStore';
import { green, mintGreen, placeholder, red } from '@/shared/styles/colors';
import { Muted, Panel, SectionTitle, Strong } from './AdminUI';
import { hourly } from './format';

// Same limits as the server (mutations.adminSchema.ts)
const MIN_RATE_DOLLARS = 10;
const MAX_RATE_DOLLARS = 5000;

const serverMessage = (error: unknown) =>
  error instanceof ApolloError ? error.graphQLErrors[0]?.message : undefined;

const toDollars = (cents?: number | null) => (cents ? `${cents / 100}` : '');

/** Cents, null for empty (the tier's rate), or undefined when it's invalid */
const toCents = (dollars: string) => {
  const text = dollars.trim().replace(/^\$/, '');
  if (!text) {
    return null;
  }
  const value = Number(text);
  return Number.isInteger(value) &&
    value >= MIN_RATE_DOLLARS &&
    value <= MAX_RATE_DOLLARS
    ? value * 100
    : undefined;
};

const isAddressEmpty = (address: AddressFormData | null) =>
  !address ||
  (!address.city &&
    !address.addressLine1.trim() &&
    !address.addressLine2.trim() &&
    !address.zipCode.trim());

/**
 * What staff set so clients can book a stylist: rates (stylists can't set
 * them), and the in-person switch plus address. Inline rather than a modal:
 * the dropdowns render in a portal that would sit behind one on web.
 */
const StylistBookingEditor = ({
  stylist,
  isDesktop,
  onClose,
  onSaved,
}: {
  stylist: AdminStylist;
  isDesktop: boolean;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) => {
  const { setSnackbarState } = useStore();
  const [videoRate, setVideoRate] = useState(
    toDollars(stylist.customHourlyRate),
  );
  const [inPersonRate, setInPersonRate] = useState(
    toDollars(stylist.customInPersonHourlyRate),
  );
  const [isAvailableInPerson, setIsAvailableInPerson] = useState(
    stylist.isAvailableInPerson,
  );
  const [address, setAddress] = useState<AddressFormData | null>(null);
  const [isAddressValid, setIsAddressValid] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [save, { loading: saving }] = useMutation<
    MutationResult<'adminUpdateStylistBooking'>,
    MutationAdminUpdateStylistBookingArgs
  >(ADMIN_UPDATE_STYLIST_BOOKING);

  const tierRate = hourly(stylist.tierHourlyRate);

  // Only what changed is sent, so a rate or address the form can't show
  // exactly (or old data the checks would now refuse) is left alone
  const changedRate = (text: string, saved?: number | null) =>
    text.trim() === toDollars(saved) ? undefined : toCents(text);

  const onSave = async () => {
    const customHourlyRate = changedRate(videoRate, stylist.customHourlyRate);
    const customInPersonHourlyRate = changedRate(
      inPersonRate,
      stylist.customInPersonHourlyRate,
    );
    const isRateInvalid = (text: string, saved?: number | null) =>
      text.trim() !== toDollars(saved) && toCents(text) === undefined;
    if (
      isRateInvalid(videoRate, stylist.customHourlyRate) ||
      isRateInvalid(inPersonRate, stylist.customInPersonHourlyRate)
    ) {
      setFormError(
        `Rates are whole dollars from $${MIN_RATE_DOLLARS} to $${MAX_RATE_DOLLARS} an hour.`,
      );
      return;
    }
    if (isAddressEmpty(address)) {
      if (stylist.address) {
        setFormError(
          'Addresses can’t be removed here. Change it, or cancel to keep it.',
        );
        return;
      }
    } else if (!isAddressValid) {
      setFormError('Finish the address: city, street and zip code.');
      return;
    }
    const addressInput =
      address?.city && address.state && isAddressValid
        ? {
            // Same shape the app saves: line 2 on its own line
            streetAddress: [address.addressLine1, address.addressLine2]
              .map((line) => line.trim())
              .filter(Boolean)
              .join('\n'),
            city: address.city,
            state: address.state,
            zipCode: address.zipCode.trim(),
            country: Country.Usa,
            locationType: mapLocationTypeToEnum(address.locationType),
            accessDetails: address.accessDetails.trim() || null,
          }
        : null;
    const saved = stylist.address;
    const isAddressChanged =
      addressInput !== null &&
      (!saved ||
        saved.streetAddress !== addressInput.streetAddress ||
        saved.city !== addressInput.city ||
        saved.state !== addressInput.state ||
        saved.zipCode !== addressInput.zipCode ||
        saved.locationType !== addressInput.locationType ||
        (saved.accessDetails || null) !== addressInput.accessDetails);

    const inPersonRateAfter =
      customInPersonHourlyRate === undefined
        ? stylist.customInPersonHourlyRate
        : customInPersonHourlyRate;
    if (isAvailableInPerson && !inPersonRateAfter) {
      setFormError('Add an in-person rate to offer in-person sessions.');
      return;
    }
    if (isAvailableInPerson && !addressInput && !saved) {
      setFormError('Add an address to offer in-person sessions.');
      return;
    }

    setFormError(null);
    try {
      await save({
        variables: {
          stylistId: stylist.stylistId,
          input: {
            isAvailableInPerson,
            customHourlyRate,
            customInPersonHourlyRate,
            address: isAddressChanged ? addressInput : undefined,
          },
        },
      });
    } catch (saveError) {
      SentryService.captureError(
        'Admin failed to save stylist booking settings',
        saveError,
      );
      setFormError(serverMessage(saveError) ?? 'Couldn’t save those changes.');
      return;
    }

    // Saved either way, so a failed refresh mustn't invite another save
    let refreshed = true;
    try {
      await onSaved();
    } catch (refreshError) {
      refreshed = false;
      SentryService.captureError(
        'Admin failed to refresh stylists',
        refreshError,
      );
    }
    setSnackbarState({
      open: true,
      message: refreshed
        ? `Saved ${stylist.name}’s booking settings`
        : `Saved ${stylist.name}’s booking settings, but couldn’t refresh the list`,
      severity: 'success',
    });
    onClose();
  };

  const rateHint = tierRate
    ? `Leave empty for their tier rate (${tierRate})`
    : 'No pricing tier yet, and they can’t be booked until they have one';

  return (
    <Panel style={{ marginVertical: 8, paddingHorizontal: 24 }}>
      <SectionTitle style={{ marginBottom: 4 }}>
        {stylist.name}’s booking
      </SectionTitle>
      <View
        style={{
          flexDirection: isDesktop ? 'row' : 'column',
          gap: isDesktop ? 48 : 0,
          alignItems: 'flex-start',
        }}
      >
        <Column>
          <SectionHeading>Video sessions</SectionHeading>
          <InputContainer>
            <FieldLabel>Video rate ($ an hour)</FieldLabel>
            <FieldInput
              placeholder={tierRate ? `${tierRate} (tier rate)` : 'e.g. 200'}
              placeholderTextColor={placeholder}
              value={videoRate}
              inputMode="numeric"
              onChangeText={setVideoRate}
              editable={!saving}
            />
            <HelpText>{rateHint}.</HelpText>
          </InputContainer>

          <SectionHeading>In-person sessions</SectionHeading>
          <TouchableOpacity
            onPress={() => setIsAvailableInPerson((on) => !on)}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: isAvailableInPerson }}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
              marginBottom: 8,
            }}
          >
            <Checkbox
              checked={isAvailableInPerson}
              onPress={() => setIsAvailableInPerson((on) => !on)}
            />
            <FieldLabel style={{ marginBottom: 0 }}>
              Clients can book them in person
            </FieldLabel>
          </TouchableOpacity>
          <HelpText style={{ marginTop: 0, marginBottom: 20 }}>
            Needs an in-person rate and an address.
          </HelpText>
          <InputContainer>
            <FieldLabel>In-person rate ($ an hour)</FieldLabel>
            <FieldInput
              placeholder="e.g. 300"
              placeholderTextColor={placeholder}
              value={inPersonRate}
              inputMode="numeric"
              onChangeText={setInPersonRate}
              editable={!saving}
            />
          </InputContainer>
        </Column>

        <Column>
          <SectionHeading>Address</SectionHeading>
          <AddressForm
            user={{ address: stylist.address }}
            onAddressChange={setAddress}
            onValidChange={setIsAddressValid}
            disabled={saving}
          />
        </Column>
      </View>

      <View style={{ width: '100%', maxWidth: 480, gap: 10 }}>
        {formError ? <Muted style={{ color: red }}>{formError}</Muted> : null}
        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1 }}>
            <Button
              text="Save"
              variant="primary"
              size="small"
              loading={saving}
              disabled={saving}
              onPress={onSave}
            />
          </View>
          <View style={{ flex: 1 }}>
            <Button
              text="Cancel"
              variant="secondary"
              size="small"
              disabled={saving}
              onPress={onClose}
            />
          </View>
        </View>
      </View>
    </Panel>
  );
};

const Column = styled.View({
  flex: 1,
  width: '100%',
  maxWidth: 480,
});

/** Divides the form into its parts, like the app's own settings pages */
const SectionHeading = styled(Strong)({
  color: green,
  borderTopWidth: 1,
  borderTopColor: mintGreen,
  paddingTop: 16,
  marginTop: 12,
  marginBottom: 16,
});

export default StylistBookingEditor;
